import { useEffect, useRef } from 'react'

import { PauseIcon, TrashIcon, WelleIcon, PfeilHochIcon } from '../../components/Icons'
import { dauer } from '../../lib/sprachaufnahme'

/**
 * The input line while a voice message is being recorded (#63).
 *
 * From the log, and this time as a finished design: „wenn man das Wellensymbol
 * drückt beginnt die Aufnahme. gleichzeitig erscheint aber schon der
 * Senden-Knopf (neben dem Stop-Knopf). Zum Beenden + Senden dann direkt den
 * Senden-Knopf drücken. der Pause Knopf pausiert, bzw. kann man dann weiter
 * aufnehmen. die Eingabezeile verschwindet ganz, stattdessen kann der Platz
 * für die Anzeige der Symbole (Pause) genutzt werden. direkt darüber ein
 * Symbol für die Sprachnachricht (idealerweise die Wellenform und ein Zähler
 * über die Länge)."
 *
 * **Two steps where one was meant.** Until now: stop, then send — and between
 * the two stood a text field with nothing to do.
 *
 * ```
 * ┌─────────────────────────────────────────┐
 * │  0:14   ▂▅▇▅▂▃▇▅▂▁▃▅▂ · · · · · · · ·   │  counter + waveform
 * ├─────────────────────────────────────────┤
 * │   🗑 Verwerfen   ⏸ Pause     ➤ Senden   │  three buttons, each ≥ 44 × 44
 * └─────────────────────────────────────────┘
 * ```
 */
export function Aufnahmeleiste({
  dauerMs,
  pausiert,
  kannPausieren,
  pegel,
  onVerwerfen,
  onPause,
  onWeiter,
  onSenden,
}: {
  dauerMs: number
  pausiert: boolean
  kannPausieren: boolean
  /** The current level between 0 and 1 — read per frame, never stored. */
  pegel: () => number
  onVerwerfen: () => void
  onPause: () => void
  onWeiter: () => void
  onSenden: () => void
}) {
  return (
    <div className={pausiert ? 'aufnahme aufnahme--pausiert' : 'aufnahme'} role="group" aria-label="Sprachnachricht aufnehmen">
      <div className="aufnahme__spur">
        {/* **Ein angehaltenes Mikrofon sieht sonst aus wie ein laufendes.**
            Der Punkt pulst, solange aufgenommen wird, und ist in der Pause
            weg; die Zahl daneben wird blass. Zwei Zeichen für denselben
            Zustand, weil eines davon eine Farbe ist -- und wer Farben nicht
            unterscheidet, sieht immer noch, dass der Punkt fehlt. */}
        {!pausiert && <span className="composer__punkt" aria-hidden="true" />}
        <span className="aufnahme__zeit">{dauer(dauerMs)}</span>
        <Welle pegel={pegel} pausiert={pausiert} />
      </div>

      <div className="aufnahme__knoepfe">
        <button
          type="button"
          className="aufnahme__knopf"
          onClick={onVerwerfen}
          aria-label="Aufnahme verwerfen"
          title="Verwerfen"
        >
          <TrashIcon size={22} />
        </button>

        {/* **Kann der Browser nicht anhalten, steht hier nichts.** Eine Taste,
            die nichts tut, ist schlimmer als eine fehlende. */}
        {kannPausieren ? (
          <button
            type="button"
            className="aufnahme__knopf aufnahme__knopf--halt"
            onClick={pausiert ? onWeiter : onPause}
            aria-label={pausiert ? 'Weiter aufnehmen' : 'Aufnahme anhalten'}
            title={pausiert ? 'Weiter' : 'Pause'}
          >
            {pausiert ? <WelleIcon size={20} /> : <PauseIcon size={20} />}
          </button>
        ) : (
          <span className="aufnahme__knopf" aria-hidden="true" />
        )}

        {/* **Senden beendet und schickt in einem Druck** — das ist der Kern
            der Meldung. Kein Zwischenzustand, in dem man noch einmal drücken
            müsste. */}
        <button
          type="button"
          className="aufnahme__knopf aufnahme__knopf--senden"
          onClick={onSenden}
          aria-label="Sprachnachricht senden"
          title="Senden"
        >
          <PfeilHochIcon size={22} />
        </button>
      </div>
    </div>
  )
}

/** How long one bar of the waveform stands for. */
const BALKEN_MS = 200
/** Width of one bar plus its gap, in CSS pixels. */
const BALKEN_BREITE = 4

/**
 * The waveform — drawn, not stored in state.
 *
 * **Nothing here belongs in `useState`.** A repaint per animation frame would
 * re-render the whole composer sixty times a second; the levels live in a ref
 * and go straight onto the canvas. The counter beside it is text and does
 * re-render — once every tenth of a second, which is what it is worth.
 *
 * **The bars scroll, the rest is dots.** Two minutes at 200 ms per bar is 600
 * bars and no phone is that wide, so the strip shows the recent seconds and
 * the oldest fall off the left. What has not been spoken yet is a dotted line
 * — the same shape the picture from the log has.
 */
function Welle({ pegel, pausiert }: { pegel: () => number; pausiert: boolean }) {
  const flaeche = useRef<HTMLCanvasElement>(null)
  const werte = useRef<number[]>([])
  const zuletzt = useRef(0)
  const angehalten = useRef(pausiert)
  angehalten.current = pausiert

  useEffect(() => {
    const leinwand = flaeche.current
    if (leinwand === null) return
    const stift = leinwand.getContext('2d')
    if (stift === null) return

    /*
     * **Die Größe kommt aus dem Kasten, nicht aus einer Zahl.** Ein Canvas
     * hat zwei Größen — die im Stylesheet und die seiner Bildpunkte —, und
     * wer sie auseinanderlaufen lässt, malt unscharf. Beim Drehen des Geräts
     * ändert sich die erste; der Beobachter zieht die zweite nach.
     */
    const messen = () => {
      const dichte = window.devicePixelRatio || 1
      leinwand.width = Math.max(1, Math.round(leinwand.clientWidth * dichte))
      leinwand.height = Math.max(1, Math.round(leinwand.clientHeight * dichte))
      stift.setTransform(dichte, 0, 0, dichte, 0, 0)
    }
    messen()
    const beobachter = new ResizeObserver(messen)
    beobachter.observe(leinwand)

    let laeuft = true
    const male = (jetzt: number) => {
      if (!laeuft) return

      if (!angehalten.current && jetzt - zuletzt.current >= BALKEN_MS) {
        zuletzt.current = jetzt
        werte.current.push(pegel())
      }

      const breite = leinwand.clientWidth
      const hoehe = leinwand.clientHeight
      const passen = Math.max(1, Math.floor(breite / BALKEN_BREITE))
      if (werte.current.length > passen) {
        werte.current = werte.current.slice(werte.current.length - passen)
      }

      /* **Die Farben stehen im Stylesheet und werden hier gelesen.** Ein
         Farbwert im Code wäre die 48. Stelle, die an `tokens.css` vorbeigeht
         — die Regel dazu steht in `CLAUDE.md`. */
      const stil = getComputedStyle(leinwand)
      const an = stil.color
      const aus = stil.getPropertyValue('--welle-aus').trim() || stil.color

      stift.clearRect(0, 0, breite, hoehe)
      const mitte = hoehe / 2

      werte.current.forEach((wert, i) => {
        const x = i * BALKEN_BREITE
        // Mindestens zwei Punkte hoch: Stille ist eine Linie, kein Loch.
        const hoch = Math.max(2, wert * (hoehe - 4))
        stift.fillStyle = an
        stift.fillRect(x, mitte - hoch / 2, BALKEN_BREITE - 1.5, hoch)
      })

      stift.fillStyle = aus
      for (let x = werte.current.length * BALKEN_BREITE; x < breite; x += BALKEN_BREITE) {
        stift.fillRect(x, mitte - 1, 1.5, 2)
      }

      requestAnimationFrame(male)
    }
    requestAnimationFrame(male)

    return () => {
      laeuft = false
      beobachter.disconnect()
    }
  }, [pegel])

  return <canvas ref={flaeche} className="aufnahme__welle" aria-hidden="true" />
}
